import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { propertyApi, favoriteApi, Property } from '../services/api';

/**
 * 분양 현장 상세 정보 관리 Hook
 * - ID 또는 house_manage_no(주택관리번호) 모두 지원
 * - 예: usePropertyDetail('44') 또는 usePropertyDetail('2021000959')
 */
export const usePropertyDetail = (propertyId: string | undefined) => {
  const navigate = useNavigate();
  const [property, setProperty] = useState<Property | null>(null);
  const [loading, setLoading] = useState(true);
  // v2 S3: 404 가 아닌 로드 실패(네트워크·5xx)를 구분 — 오류코드(E-503/E-NET) 표기용 (2026-09-21)
  const [loadError, setLoadError] = useState<{ code: string; at: Date } | null>(null);
  const [isFavorited, setIsFavorited] = useState(false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);

  useEffect(() => {
    if (propertyId) {
      loadPropertyDetail(propertyId);
      trackRecentView(propertyId);
    }
  }, [propertyId]);

  const loadPropertyDetail = async (id: string) => {
    try {
      setLoading(true);
      setLoadError(null);
      const data = await propertyApi.getDetail(id);
      setProperty(data);
      setIsFavorited(data.is_favorited || false);
    } catch (error) {
      console.error('Failed to load property detail:', error);
      const status = (error as any)?.response?.status;
      if (status !== 404) {
        setLoadError({ code: typeof status === 'number' ? `E-${status}` : 'E-NET', at: new Date() });
      }
    } finally {
      setLoading(false);
    }
  };

  const trackRecentView = async (id: string) => {
    try {
      const token = localStorage.getItem('access_token');
      if (token) {
        await propertyApi.getDetail(id);
      }
    } catch (error) {
      console.error('Failed to track recent view:', error);
    }
  };

  const handleFavoriteToggle = async () => {
    if (!property) return;

    const token = localStorage.getItem('access_token');
    if (!token) {
      alert('로그인이 필요한 서비스입니다.');
      navigate('/login');
      return;
    }

    try {
      setFavoriteLoading(true);
      await favoriteApi.toggle(property.id);
      setIsFavorited(!isFavorited);
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      alert('찜하기 처리 중 오류가 발생했습니다.');
    } finally {
      setFavoriteLoading(false);
    }
  };

  const reload = () => {
    if (propertyId) loadPropertyDetail(propertyId);
  };

  return {
    property,
    loading,
    loadError,
    reload,
    isFavorited,
    favoriteLoading,
    handleFavoriteToggle,
  };
};
